import { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import AssignmentService from "../services/AssignmentService";
import type { Assignment } from "../services/AssignmentService";

const statusColor: Record<string, "default" | "success" | "warning" | "error"> = {
  en_cours: "warning",
  terminee: "success",
  terminée: "success",
  annulee: "error",
  annulée: "error",
};

const formatDateTime = (date?: string, time?: string) => {
  if (!date) return "-";
  return `${date}${time ? ` ${time}` : ""}`;
};

const AssignmentsHistory = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await AssignmentService.getAll();
        // Trier par date descendante
        const sorted = [...data].sort((a, b) =>
          (b.date || "").localeCompare(a.date || "")
        );
        setAssignments(sorted);
        setError(null);
      } catch (err) {
        console.error(err);
        setError("Impossible de charger l'historique des affectations.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Historique des affectations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Toutes les affectations (en cours et terminées)
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/assignments")}
          >
            Retour aux affectations
          </Button>
          <Button
            variant="contained"
            startIcon={<HistoryIcon />}
            onClick={() => window.location.reload()}
          >
            Rafraîchir
          </Button>
        </Box>
      </Box>

      <Paper elevation={2}>
        {loading ? (
          <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : assignments.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="info">Aucune affectation pour le moment.</Alert>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Véhicule</TableCell>
                <TableCell>Chauffeur</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Début</TableCell>
                <TableCell>Fin</TableCell>
                <TableCell>Étape</TableCell>
                <TableCell align="right">Km départ</TableCell>
                <TableCell align="right">Km fin</TableCell>
                <TableCell align="right">Détails</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.map((a) => (
                <TableRow key={a._id} hover>
                  <TableCell>{a.vehiclePlate || "-"}</TableCell>
                  <TableCell>
                    {a.driverFirstName || a.driverLastName
                      ? `${a.driverFirstName || ""} ${a.driverLastName || ""}`.trim()
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={a.status}
                      color={statusColor[a.status] || "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(a.date, a.time)}</TableCell>
                  <TableCell>{formatDateTime((a as any).endDate, (a as any).endTime)}</TableCell>
                  <TableCell>{a.currentStep || "-"}</TableCell>
                  <TableCell align="right">{a.startMileage ?? "-"}</TableCell>
                  <TableCell align="right">{(a as any).endMileage ?? "-"}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="text"
                      size="small"
                      onClick={() =>
                        navigate(`/vehicules/historique/${a.vehiclePlate}/processus/${a._id}`)
                      }
                    >
                      Voir
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Container>
  );
};

export default AssignmentsHistory;

